import { allCountries, US } from 'country-region-data'
import { useCookies } from 'react-cookie'
import { useSelector } from 'react-redux'
import {
  ICountryWorld,
  IDataCountriesReducer,
  IDealersAndAgentsReducer,
  IDealerWorld,
  IReducer,
  IRecaptchaToken
} from 'src/interface'

export const isToday = (date: Date) => {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

/**
 * Converts a string to a slug by replacing spaces with "-" and removing special characters.
 *
 * @param text The input string to be converted.
 * @returns The formatted slug.
 */
export const convertToSlug = (text: string) => {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-')
}

/**
 * Gets the country name searching by country code.
 *
 * @param countryCode The country code to search by.
 * @returns The complete name.
 */
export const getCountryName = (countryCode: string) =>
  allCountries.find((countryToFind) => countryToFind[1] === countryCode.toUpperCase())?.[0] || countryCode

/**
 * Returns a list of international dealers filtered by the given country name.
 * Optionally excludes the dealer 'Chris Burger' from the results.
 *
 * @param countryName - The country name used to filter dealers.
 * @param dealersReducer - Reducer containing the full list of dealers.
 * @param countriesReducer - Reducer containing country information.
 * @param includeChris - If false, excludes 'Chris Burger' from the list (default: false).
 * @returns A filtered array of international dealers matching the specified criteria.
 */
export const getInternationalDealers = (
  countryName: string | undefined,
  dealersReducer: IDealersAndAgentsReducer,
  countriesReducer: IDataCountriesReducer,
  includeChris: boolean = false
) => {
  const countryCode = allCountries.find(([country]) => country === countryName)?.[1]?.toLowerCase()
  if (!countryCode) {
    return []
  }
  const dealers = dealersReducer.dealersAgents.dealers
  const selectedCountry = countriesReducer.countries.find(
    (country: ICountryWorld) => country.COUNTRYCODE === countryCode.toLowerCase()
  )
  //TODO: should be refactor when Chris Burger is removed from the list permanently
  const filteredDealers = dealers.filter((element: IDealerWorld) => {
    return (
      (element.Country === null || element.Country === selectedCountry?.DISPLAY) &&
      (includeChris || element.Contact !== 'Chris Burger')
    )
  })
  return filteredDealers
}

/**
 * Returns a list of domestic dealers filtered by the given state.
 *
 * @param stateSelected - The state used to filter dealers.
 * @param dataContactReducer - Reducer containing the full list of dealers.
 * @returns A filtered array of domestic dealers matching the specified criteria.
 */
export const getDomesticDealers = (stateSelected: string, dataContactReducer: IDealersAndAgentsReducer) => {
  const stateCode: string = US[2].find((state) => state[0] === stateSelected)?.[1] || ''
  const dealersByState = dataContactReducer.dealersAgents.domestic.filter((item) => {
    return item.states.includes(stateCode)
  })
  const domesticDealers = dealersByState.map((dealer) => {
    return {
      Email: dealer.email,
      Contact: dealer.name,
      PhoneNumber: dealer.tollFreePhone,
      ProductsList: []
    }
  })
  return domesticDealers
}

export const getFormData = () => {
  const [cookies] = useCookies(['country_code'])
  const cookieCountryCode = cookies.country_code?.toUpperCase()
  const userInfo = useSelector((state: IReducer) => state.accountReducer.userInfo)
  const isAuthenticated = useSelector((state: IReducer) => state.accountReducer.isAuthenticated)
  const dataCountriesReducer = useSelector((state: IReducer) => state.dataCountriesReducer)
  const dataContactReducer = useSelector((state: IReducer) => state.dataContactReducer)

  return { cookieCountryCode, userInfo, isAuthenticated, dataCountriesReducer, dataContactReducer }
}

export const getRecaptchaToken = (recaptchaToken: IRecaptchaToken): string => {
  if (process.env.REACT_APP_is_test === 'true') {
    return 'fake-recaptcha-token-for-test'
  }
  if (typeof recaptchaToken === 'string') {
    return recaptchaToken
  }
  throw new Error('Error in Recaptcha')
}
